/**
 * VendedorLayout — sidebar/layout do painel do vendedor.
 * Mesma estrutura visual do ClienteLayout, itens específicos do vendedor.
 */
import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Home, ShoppingCart, TrendingUp, ShoppingBag, MessageCircle,
  Settings, LogOut, Menu, User as UserIcon, Sun, Moon,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { applyThemeColors, getContrastText } from './ThemeColorsCard';
import ChatWidget from './ChatWidget';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const navItems = [
  { to: '/vendedor/dashboard', icon: Home, label: 'Início' },
  { to: '/vendedor/pedido', icon: ShoppingCart, label: 'Gerar Pedido', highlight: true },
  { to: '/vendedor/vendas', icon: TrendingUp, label: 'Minhas Vendas' },
  { to: '/vendedor/compras', icon: ShoppingBag, label: 'Minhas Compras' },
  { to: '#chat', icon: MessageCircle, label: 'Chat', action: 'openChat' },
  { to: '/vendedor/profile', icon: Settings, label: 'Configurações' },
];

export default function VendedorLayout() {
  const navigate = useNavigate();
  const { user, logout, loading } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [storeLogo, setStoreLogo] = useState('');
  const [themeColors, setThemeColors] = useState({});

  useEffect(() => {
    if (loading) return;
    if (!user || user.role !== 'vendedor') { navigate('/login'); return; }
    axios.get(`${API}/public/settings`)
      .then(r => setStoreLogo(r.data?.logo_base64 || r.data?.logo || ''))
      .catch(() => {});
    // Carrega cores personalizadas do vendedor
    axios.get(`${API}/vendedor/me/profile`)
      .then(r => {
        const c = r.data?.theme_colors || {};
        setThemeColors(c);
        applyThemeColors(c);
      })
      .catch(() => {});
  }, [loading, user, navigate]);

  const handleLogout = async () => { await logout(); navigate('/login'); };

  if (loading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const Sidebar = (
    <aside className="w-64 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 flex flex-col h-full" data-testid="vendedor-sidebar">
      <div className="p-5 border-b border-gray-200 dark:border-gray-700">
        <div className="flex flex-col items-center gap-2">
          {storeLogo ? (
            <img src={storeLogo} alt="Loja" className="h-20 w-auto max-w-full object-contain" />
          ) : (
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-blue-600 to-purple-600 flex items-center justify-center text-white font-bold text-2xl">
              SA
            </div>
          )}
          <div className="text-center">
            <p className="font-bold text-sm text-gray-900 dark:text-white">Painel do Vendedor</p>
            <p className="text-[10px] text-gray-500">Santa Ana Distribuidora</p>
          </div>
        </div>
      </div>

      <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center overflow-hidden">
            {user.profile_image ? (
              <img src={user.profile_image} alt="" className="w-full h-full object-cover" />
            ) : (
              <UserIcon className="w-4 h-4 text-blue-700 dark:text-blue-300" />
            )}
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold truncate text-gray-900 dark:text-white">{user.name || user.email}</p>
            <p className="text-[10px] text-gray-500 truncate">{user.email}</p>
          </div>
        </div>
      </div>

      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
        {navItems.map(item => (
          item.action === 'openChat' ? (
            <button
              key={item.to}
              onClick={() => { setMobileOpen(false); if (window.openChat) window.openChat(); }}
              className="w-full group flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-all"
              data-testid={`vendedor-nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
            >
              <item.icon className="w-4 h-4" />
              <span className="flex-1 text-left">{item.label}</span>
            </button>
          ) : (
            <NavLink
              key={item.to}
              to={item.to}
              end
              onClick={() => setMobileOpen(false)}
              className={({ isActive }) =>
                `group flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                  isActive
                    ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-700'
                    : item.highlight
                    ? 'text-emerald-700 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 font-semibold'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`
              }
              data-testid={`vendedor-nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
            >
              <item.icon className={`w-4 h-4 ${item.highlight ? 'text-emerald-600' : ''}`} />
              <span className="flex-1">{item.label}</span>
            </NavLink>
          )
        ))}
      </nav>

      <div className="p-3 border-t border-gray-200 dark:border-gray-700 space-y-2">
        <button
          onClick={toggleTheme}
          className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
          data-testid="vendedor-theme-toggle"
          title={theme === 'light' ? 'Modo Escuro' : 'Modo Claro'}
        >
          {theme === 'light' ? <><Moon className="w-4 h-4" /> Modo Escuro</> : <><Sun className="w-4 h-4" /> Modo Claro</>}
        </button>
        <button
          onClick={handleLogout}
          className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
          data-testid="vendedor-logout-btn"
        >
          <LogOut className="w-4 h-4" />
          Sair
        </button>
      </div>
    </aside>
  );

  return (
    <div
      className="min-h-screen flex bg-gray-50 dark:bg-gray-900"
      style={themeColors.bg ? { background: themeColors.bg, color: getContrastText(themeColors.bg) } : undefined}
    >
      <div className="hidden lg:block flex-shrink-0">{Sidebar}</div>

      {mobileOpen && (
        <>
          <div
            className="lg:hidden fixed inset-0 z-40 bg-black/50"
            onClick={() => setMobileOpen(false)}
            data-testid="vendedor-sidebar-overlay"
          />
          <div className="lg:hidden fixed inset-y-0 left-0 z-50 w-64 shadow-2xl">
            {Sidebar}
          </div>
        </>
      )}

      <div className="flex-1 flex flex-col min-w-0">
        <header className="lg:hidden bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-3 py-2 flex items-center justify-between">
          <button
            onClick={() => setMobileOpen(true)}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
            data-testid="vendedor-sidebar-toggle"
          >
            <Menu className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2">
            {storeLogo ? (
              <img src={storeLogo} alt="Loja" className="h-12 w-auto max-w-[180px] object-contain mx-auto" />
            ) : (
              <span className="font-bold text-sm">Santa Ana</span>
            )}
          </div>
          <div className="w-9" />
        </header>

        <main className="flex-1 overflow-auto">
          <Outlet />
        </main>
      </div>

      <ChatWidget />
    </div>
  );
}
